import { FC, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { MetricBreakdownDetail } from 'hooks/useRoiAppointmentMetricBreakdown';
import { formatUsdCurrency } from 'utils/formatUsdCurrency';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const BAR_COLOR = '#B01BAE';

interface MetricProductionChartProps {
  details: MetricBreakdownDetail[];
}

const MetricProductionChart: FC<MetricProductionChartProps> = ({ details }) => {
  const total = useMemo(
    () => details.reduce((sum, d) => sum + d.value, 0),
    [details]
  );

  const maxValue = useMemo(
    () => Math.max(...details.map((d) => d.value), 1),
    [details]
  );

  const chartData = useMemo(
    () => ({
      labels: details.map((d) => d.label),
      datasets: [
        {
          label: 'Production',
          data: details.map((d) => d.value),
          backgroundColor: BAR_COLOR,
          borderRadius: 2,
          barThickness: 8,
        },
      ],
    }),
    [details]
  );

  return (
    <div className="h-[52rem]">
      <Bar
        data={chartData}
        options={{
          indexAxis: 'y',
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: true, axis: 'y' },
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: true,
              mode: 'index',
              intersect: true,
              axis: 'y',
              callbacks: {
                title: (items) => items[0].label ?? '',
                label: (item) => {
                  const value = item.raw as number;
                  const pct =
                    total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                  return `${formatUsdCurrency(value)} (${pct}%)`;
                },
              },
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              max: Math.ceil(maxValue * 1.1),
              grid: {
                color: '#E8E8E8',
              },
              ticks: {
                color: '#373737',
                font: { size: 12 },
                callback: (value: any) =>
                  Number.isInteger(Number(value))
                    ? Number(value).toLocaleString()
                    : null,
              },
            },
            y: {
              grid: { display: false },
              ticks: {
                color: '#373737',
                font: { size: 14, weight: 'normal' },
              },
            },
          },
        }}
      />
    </div>
  );
};

export default MetricProductionChart;
